import { from, of } from 'rxjs';
import { switchMap, catchError, map, flatMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { dfimAxios as axios } from '../config/Axios';
import { getInSession } from './../persistence/session';
import { getInStorage } from './../persistence/storage';
import { getAllUsers } from './../actions/User';
import { getAllCompanies } from './../actions/Company';
import { getUserAudits, getAuditTypes } from './../actions/Audit';
import {
  LOGIN,
  LOGOUT,
  GET_CURRENT_PRINCIPAL,
  AUTO_LOGIN,
  GET_USER_ROLES,
  getUserRoles,
  getUserRolesSuccess,
  getUserRolesError,
  loginSuccess,
  loginError,
  logoutSuccess,
  logoutError,
  rememberMe,
  forgetMe,
  getCurrentPrincipal,
  getCurrentPrincipalSuccess,
  getCurrentPrincipalError,
} from '../actions/Authentication';

// Requests
const rolesGetRequest = () =>
  axios.get('/api/authentication/roles').then((response) => response.data);
const loginRequest = (credentials) =>
  axios.post('/api/authentication/token', credentials).then((response) => response.data);
const logoutRequest = () => new Promise((resolve) => resolve());
const principalRequest = () =>
  axios.get('/api/authentication/principal').then((response) => response.data);
const autoLoginPromise = () =>
  new Promise((resolve, reject) => {
    let accessToken = getInSession('dfim-jwt-access-token');
    let expirationDate = new Date(getInSession('dfim-jwt-expires'));

    if (!accessToken) {
      accessToken = getInStorage('dfim-jwt-access-token');
      expirationDate = new Date(getInStorage('dfim-jwt-expires'));
    }

    if (!accessToken) {
      reject(Error('No Access Token present for autologin'));
    }
    if (expirationDate <= new Date()) {
      reject(Error('Access Token is expired'));
    }

    const resultSuccess = {
      result: {
        token: {
          accessToken: accessToken,
          expirationDate: expirationDate,
        },
      },
      status: {
        code: '101',
        message: 'login successful',
      },
    };

    resolve(resultSuccess);
  });

const getAllFunctionsAfterLoginSuccess = (data) => {
  switch (data.result.user.authority.toLowerCase()) {
    case 'admin':
    case 'superuser':
      return [
        getCurrentPrincipalSuccess(data),
        getAuditTypes(),
        getUserRoles(),
        getAllUsers(data),
        getAllCompanies(data),
        getUserAudits({
          user: data.result.user,
          filters: [],
          page: 0,
          size: 50,
          sortColumn: 'created',
          sortOrder: 'DESC',
        }),
      ];

    default:
      return [
        getCurrentPrincipalSuccess(data),
        getAuditTypes(),
        getUserAudits({
          user: data.result.user,
          filters: [],
          page: 0,
          size: 50,
          sortColumn: 'created',
          sortOrder: 'DESC',
        }),
      ];
  }
};

// Epics
export const getUserRolesAllEpic = (action$) =>
  action$.pipe(
    ofType(GET_USER_ROLES),
    switchMap(() =>
      from(rolesGetRequest()).pipe(
        map((data) => getUserRolesSuccess(data)),
        catchError((error) => of(getUserRolesError(error)))
      )
    )
  );

export const loginEpic = (action$) =>
  action$.pipe(
    ofType(LOGIN),
    switchMap((action) =>
      from(loginRequest(action.credentials)).pipe(
        flatMap((data) => [
          loginSuccess(data),
          rememberMe(Object.assign(data, { rememberMe: action.rememberMe })),
          getCurrentPrincipal(),
        ]),
        catchError((error) => of(loginError(error)))
      )
    )
  );

export const getPrincipalEpic = (action$) =>
  action$.pipe(
    ofType(GET_CURRENT_PRINCIPAL),
    switchMap(() =>
      from(principalRequest()).pipe(
        flatMap((data) => getAllFunctionsAfterLoginSuccess(data)),
        catchError((error) => of(getCurrentPrincipalError(error)))
      )
    )
  );

export const logoutEpic = (action$) =>
  action$.pipe(
    ofType(LOGOUT),
    switchMap(() =>
      from(logoutRequest()).pipe(
        flatMap(() => [logoutSuccess(), forgetMe()]),
        catchError((error) => of(logoutError(error)))
      )
    )
  );

export const autoLoginEpic = (action$) =>
  action$.pipe(
    ofType(AUTO_LOGIN),
    switchMap(() =>
      from(autoLoginPromise()).pipe(
        flatMap((data) => [loginSuccess(data), getCurrentPrincipal()]),
        catchError(() => of(logoutSuccess()))
      )
    )
  );

// eslint-disable-next-line import/no-anonymous-default-export
export default [loginEpic, logoutEpic, autoLoginEpic, getPrincipalEpic, getUserRolesAllEpic];
